import { BASE_URL } from '@/config';
import { NetworkError } from '@/errors';
import { createPayment } from '@/services/payments';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import {
  getPeachErrorAction,
  paymentErrorMessages,
  waitForCheckoutSDK,
  waitForElement,
  type PaymentMessage,
  type PeachCheckoutEvent,
  type PeachCompletedCheckoutEvent,
  type PeachErrorAction,
  type PeachErrorCheckoutEvent,
} from '@/utils/peach.utils';
import { ref } from 'vue';

type UsePeachCheckoutOptions = {
  formElementId: string;
  channel: string;
  themeColor?: string;
  onCompleted: (paymentId: string) => void;
  onCancelled: () => void;
  onExpired: () => void;
  onErrorAction: (action: PeachErrorAction, paymentId: string) => void;
};

export const usePeachCheckout = (options: UsePeachCheckoutOptions) => {
  const isLoading = ref(true);
  const errorResponse = ref<PaymentMessage>({ title: '', message: '' });
  const checkoutFormLoaded = ref(false);
  const canRetryCheckout = ref(false);

  const initiate = async (paymentPlanId: string, amountCents: number, paymentMethod: string) => {
    isLoading.value = true;
    errorResponse.value = { title: '', message: '' };
    canRetryCheckout.value = false;
    checkoutFormLoaded.value = false;

    try {
      const response = await createPayment(
        paymentPlanId,
        amountCents / 100,
        `${BASE_URL}/shopper/payment`,
        paymentMethod,
        'paymentPlan',
      );

      const { _id: paymentId, checkoutId, entityId } = response;
      await loadCheckoutForm(checkoutId, entityId!, paymentId);
    } catch (error: any) {
      if (error instanceof NetworkError) {
        createWarning(error);
        errorResponse.value = { title: 'Network Error', message: 'Network error. Please try again.' };
      } else {
        createException(error);
        const msg =
          error?.response?.data ||
          'An unexpected error occurred. Payment could not be created. Please contact support.';
        errorResponse.value = { title: 'Payment Error', message: msg };
      }
      isLoading.value = false;
    }
  };

  const loadCheckoutForm = async (checkoutId: string, entityId: string, paymentId: string) => {
    const sdkLoaded = await waitForCheckoutSDK();
    isLoading.value = false;

    if (!sdkLoaded) {
      createException({
        message: 'Checkout SDK failed to load after multiple attempts.',
        checkoutId,
        userAgent: navigator.userAgent,
      });
      errorResponse.value = {
        title: 'Payment Provider Error',
        message:
          'Our payment provider failed to load. Please check your internet connection and try again. If the problem persists, please try a different browser or contact support.',
      };
      return;
    }

    try {
      const checkout = (window as any).Checkout.initiate({
        checkoutId,
        key: entityId,
        events: {
          onCompleted: (event: PeachCompletedCheckoutEvent) => {
            addMetric('peach_widget', {
              status: 'success',
              value: event.amount,
              result: event.result.code,
              channel: options.channel,
            });
            options.onCompleted(paymentId);
          },
          onCancelled: (event: PeachCheckoutEvent) => {
            addMetric('peach_widget', { status: 'cancelled', result: event.result.code, channel: options.channel });
            options.onCancelled();
          },
          onExpired: (event: PeachCheckoutEvent) => {
            addMetric('peach_widget', { status: 'expired', result: event.result.code, channel: options.channel });
            options.onExpired();
          },
          onError: (event: PeachErrorCheckoutEvent) => {
            createWarning(new Error('Peach widget error.'), {
              checkoutId,
              errorCode: event.result.code,
              errorDescription: event.result.description,
              userAgent: navigator.userAgent,
            });
            addMetric('peach_widget', { status: 'error', result: event.result.code, channel: options.channel });
            errorResponse.value = paymentErrorMessages[event.result.code] ?? {
              title: 'Payment Error',
              message: 'Something went wrong while processing the payment. Please try again or contact support.',
            };
            const action = getPeachErrorAction(event.result.code);
            canRetryCheckout.value = action === 'checkout';
            if (action) {
              options.onErrorAction(action, paymentId);
            }
          },
        },
        options: {
          theme: { brand: { primary: options.themeColor ?? '#1295a5' } },
        },
      });

      const formElement = await waitForElement(`#${options.formElementId}`);
      checkout.render(formElement);
      checkoutFormLoaded.value = true;
    } catch (error) {
      createException({
        message: 'Failed to initialize checkout form.',
        checkoutId,
        error,
        userAgent: navigator.userAgent,
      });
      errorResponse.value = {
        title: 'Payment Provider Error',
        message: 'Unable to load the payment form. Please try again or contact support.',
      };
    }
  };

  return { isLoading, errorResponse, checkoutFormLoaded, canRetryCheckout, initiate };
};
